
// src/components/admin/page-management/section-forms/FeaturedProductsForm.js
import React, { useState } from 'react';
import { 
  Info, 
  ChevronDown, 
  ChevronUp, 
  Eye, 
  EyeOff, 
  Palette, 
  Grid, 
  Search, 
  ShoppingBag, 
  ExternalLink,
  Settings,
  Code
} from 'lucide-react';
import ColorPicker from '../../shared/ColorPicker';

const FeaturedProductsForm = ({ content, updateContent }) => {
  const [expandedSections, setExpandedSections] = useState({
    title: true,
    search: false,
    grid: false,
    card: false,
    button: false,
    // spacing: false,
    api: false
  });

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const SectionHeader = ({ id, title, icon: Icon, isExpanded, description }) => (
    <button
      onClick={() => toggleSection(id)}
      className="w-full flex items-center justify-between p-4 bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-lg border border-blue-200 transition-all duration-200 group"
    >
      <div className="flex items-center space-x-3">
        <div className="p-2 bg-white rounded-lg shadow-sm group-hover:shadow-md transition-shadow">
          <Icon className="w-5 h-5 text-blue-600" />
        </div>
        <div className="text-left">
          <h4 className="font-semibold text-gray-800">{title}</h4>
          {description && (
            <p className="text-sm text-gray-600 mt-1">{description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center space-x-2">
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-blue-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-blue-600" />
        )}
      </div>
    </button>
  );

  const ToggleSwitch = ({ checked, onChange, label, description }) => (
    <div className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200 hover:border-blue-300 transition-colors">
      <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700">{label}</label>
        {description && (
          <p className="text-xs text-gray-500 mt-1">{description}</p>
        )}
      </div>
      <button
        type="button"
        onClick={() => onChange(!checked)}
        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
          checked ? 'bg-blue-600' : 'bg-gray-200'
        }`}
      >
        <span
          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
            checked ? 'translate-x-6' : 'translate-x-1'
          }`}
        />
      </button>
    </div>
  );

  const InputGroup = ({ label, description, children, required = false }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      {description && (
        <p className="text-xs text-gray-500">{description}</p>
      )}
      {children}
    </div>
  );

  const NumberInput = ({ value, onChange, min, max, placeholder, className = "" }) => (
    <div className="relative">
      <input
        type="number"
        min={min}
        max={max}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${className}`}
      />
    </div>
  );

  const TextInput = ({ value, onChange, placeholder, className = "" }) => (
    <input
      type="text"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${className}`}
    />
  );

  const Select = ({ value, onChange, options, className = "" }) => (
    <select
      value={value}
      onChange={onChange}
      className={`w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors bg-white ${className}`}
    >
      {options.map(option => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );

  const ColorPickerGroup = ({ label, color, onChange, description }) => (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">{label}</label>
      {description && (
        <p className="text-xs text-gray-500">{description}</p>
      )}
      <div className="flex items-center space-x-3 p-3 bg-white rounded-lg border border-gray-200">
        <ColorPicker
          color={color}
          onChange={onChange}
        />
      </div>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto space-y-6 p-6 bg-gray-50 rounded-xl">
      {/* Header */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center space-x-3 p-4 bg-white rounded-xl shadow-sm border border-gray-200">
          <ShoppingBag className="w-8 h-8 text-blue-600" />
          <div>
            <h3 className="text-2xl font-bold text-gray-800">Featured Products Section</h3>
            <p className="text-gray-600">Customize how your featured products are displayed</p>
          </div>
        </div>
      </div>
      
      {/* Title Settings */}
      <div className="space-y-4">
        <SectionHeader 
          id="title"
          title="Title & Branding"
          icon={Palette}
          isExpanded={expandedSections.title}
          description="Configure the section title and visual styling"
        />
        
        {expandedSections.title && (
          <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-6">
            <ToggleSwitch
              checked={content?.showTitle !== false}
              onChange={(checked) => updateContent('showTitle', checked)}
              label="Show Section Title"
              description="Display a title at the top of the featured products section"
            />
            
            {content?.showTitle !== false && (
              <div className="space-y-6 pl-4 border-l-2 border-blue-200">
                <InputGroup
                  label="Section Title"
                  description="The main heading for your featured products section"
                >
                  <TextInput
                    value={content?.title || 'Featured Products'}
                    onChange={(e) => updateContent('title', e.target.value)}
                    placeholder="Enter section title"
                  />
                </InputGroup>
                
                <ColorPickerGroup
                  label="Title Color"
                  color={content?.titleColor || '#111827'}
                  onChange={(color) => updateContent('titleColor', color)}
                  description="Choose the color for your section title"
                />
                
                <ToggleSwitch
                  checked={content?.showSeparator !== false}
                  onChange={(checked) => updateContent('showSeparator', checked)}
                  label="Show Decorative Lines"
                  description="Add elegant separator lines around the title"
                />
                
                {content?.showSeparator !== false && (
                  <div className="pl-4">
                    <ColorPickerGroup
                      label="Separator Line Color"
                      color={content?.separatorColor || '#9CA3AF'}
                      onChange={(color) => updateContent('separatorColor', color)}
                      description="Color for the decorative lines"
                    />
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Search Settings */}
      <div className="space-y-4">
        <SectionHeader 
          id="search"
          title="Search Functionality"
          icon={Search}
          isExpanded={expandedSections.search}
          description="Allow users to search through featured products"
        />
        
        {expandedSections.search && (
          <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-6">
            <ToggleSwitch
              checked={content?.searchEnabled === true}
              onChange={(checked) => updateContent('searchEnabled', checked)}
              label="Enable Product Search"
              description="Add a search box to filter featured products"
            />
            
            {content?.searchEnabled === true && (
              <div className="pl-4 border-l-2 border-green-200">
                <InputGroup
                  label="Search Box Placeholder"
                  description="Text shown in the search box when empty"
                >
                  <TextInput
                    value={content?.searchPlaceholder || 'Search featured products...'}
                    onChange={(e) => updateContent('searchPlaceholder', e.target.value)}
                    placeholder="Enter placeholder text"
                  />
                </InputGroup>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Grid Settings */}
      <div className="space-y-4">
        <SectionHeader 
          id="grid"
          title="Layout & Grid"
          icon={Grid}
          isExpanded={expandedSections.grid}
          description="Control how products are arranged and displayed"
        />
        
        {expandedSections.grid && (
          <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputGroup
                label="Maximum Products"
                description="How many products to show (1-20)"
              >
                <NumberInput
                  value={content?.maxProducts || 5}
                  onChange={(e) => updateContent('maxProducts', parseInt(e.target.value, 10))}
                  min={1}
                  max={20}
                  placeholder="5"
                />
              </InputGroup>
              
              <InputGroup
                label="Spacing Between Products"
                description="Gap size (e.g., 1.5rem, 24px)"
              >
                <TextInput
                  value={content?.gapSize || '1.5rem'}
                  onChange={(e) => updateContent('gapSize', e.target.value)}
                  placeholder="1.5rem"
                />
              </InputGroup>
            </div>
            
            <div className="border-t pt-6">
              <h5 className="font-medium text-gray-800 mb-4">Responsive Columns</h5>
              <p className="text-sm text-gray-600 mb-4">Set how many columns to show on different screen sizes</p>
              
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <InputGroup label="Mobile">
                  <Select
                    value={(content?.gridColumns?.mobile || 1).toString()}
                    onChange={(e) => {
                      const currentGridColumns = content?.gridColumns || {};
                      updateContent('gridColumns', {
                        ...currentGridColumns,
                        mobile: parseInt(e.target.value, 10)
                      });
                    }}
                    options={[
                      { value: '1', label: '1 Column' },
                      { value: '2', label: '2 Columns' }
                    ]}
                  />
                </InputGroup>
                
                <InputGroup label=" Tablet">
                  <Select
                    value={(content?.gridColumns?.tablet || 2).toString()}
                    onChange={(e) => {
                      const currentGridColumns = content?.gridColumns || {};
                      updateContent('gridColumns', {
                        ...currentGridColumns,
                        tablet: parseInt(e.target.value, 10)
                      });
                    }}
                    options={[
                      { value: '1', label: '1 Column' },
                      { value: '2', label: '2 Columns' },
                      { value: '3', label: '3 Columns' }
                    ]}
                  />
                </InputGroup>
                
                <InputGroup label="Desktop">
                  <Select
                    value={(content?.gridColumns?.desktop || 3).toString()}
                    onChange={(e) => {
                      const currentGridColumns = content?.gridColumns || {};
                      updateContent('gridColumns', {
                        ...currentGridColumns,
                        desktop: parseInt(e.target.value, 10)
                      });
                    }}
                    options={[
                      { value: '2', label: '2 Columns' },
                      { value: '3', label: '3 Columns' },
                      { value: '4', label: '4 Columns' }
                    ]}
                  />
                </InputGroup>
                
                <InputGroup label="Large Screen">
                  <Select
                    value={(content?.gridColumns?.widescreen || 4).toString()}
                    onChange={(e) => {
                      const currentGridColumns = content?.gridColumns || {};
                      updateContent('gridColumns', {
                        ...currentGridColumns,
                        widescreen: parseInt(e.target.value, 10)
                      });
                    }}
                    options={[
                      { value: '3', label: '3 Columns' },
                      { value: '4', label: '4 Columns' },
                      { value: '5', label: '5 Columns' },
                      { value: '6', label: '6 Columns' }
                    ]}
                  />
                </InputGroup>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Product Card Settings */}
      <div className="space-y-4">
        <SectionHeader 
          id="card"
          title="Product Card Design"
          icon={Eye}
          isExpanded={expandedSections.card}
          description="Customize the appearance of individual product cards"
        />
        
        {expandedSections.card && (
          <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-6">
            <InputGroup
              label="Product Image Shape"
              description="Control the aspect ratio of product images (100% = square, 75% = rectangular)"
            >
              <TextInput
                value={content?.imageAspectRatio || '100%'}
                onChange={(e) => updateContent('imageAspectRatio', e.target.value)}
                placeholder="100%"
              />
            </InputGroup>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <h5 className="font-medium text-gray-800">Content Options</h5>
                
                <ToggleSwitch
                  checked={content?.showProductDescription === true}
                  onChange={(checked) => updateContent('showProductDescription', checked)}
                  label="Show Product Description"
                  description="Display product descriptions on cards"
                />
                
                <ToggleSwitch
                  checked={content?.showPricing !== false}
                  onChange={(checked) => updateContent('showPricing', checked)}
                  label="Show Product Prices"
                  description="Display pricing information"
                />
                
                {content?.showPricing !== false && (
                  <ColorPickerGroup
                    label="Price Text Color"
                    color={content?.priceColor || '#2563EB'}
                    onChange={(color) => updateContent('priceColor', color)}
                  />
                )}
              </div>
              
              <div className="space-y-4">
                <h5 className="font-medium text-gray-800">Visual Effects</h5>
                
                <ToggleSwitch
                  checked={content?.cardShadowEnabled !== false}
                  onChange={(checked) => updateContent('cardShadowEnabled', checked)}
                  label="Card Shadow"
                  description="Add subtle shadows to product cards"
                />
                
                <ToggleSwitch
                  checked={content?.cardBorderEnabled !== false}
                  onChange={(checked) => updateContent('cardBorderEnabled', checked)}
                  label="Card Border"
                  description="Add borders around product cards"
                />
                
                {content?.cardBorderEnabled !== false && (
                  <ColorPickerGroup
                    label="Border Color"
                    color={content?.cardBorderColor || '#E5E7EB'}
                    onChange={(color) => updateContent('cardBorderColor', color)}
                  />
                )}
                
                <ToggleSwitch
                  checked={content?.hoverEffectEnabled !== false}
                  onChange={(checked) => updateContent('hoverEffectEnabled', checked)}
                  label="Hover Animation"
                  description="Add interactive hover effects"
                />
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* View All Button Settings */}
      <div className="space-y-4">
        <SectionHeader 
          id="button"
          title="Call-to-Action Button"
          icon={ExternalLink}
          isExpanded={expandedSections.button}
          description="Configure the 'View All' button to link to your full product catalog"
        />
        
        {expandedSections.button && (
          <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-6">
            <ToggleSwitch
              checked={content?.showViewAllButton !== false}
              onChange={(checked) => updateContent('showViewAllButton', checked)}
              label="Show 'View All Products' Button"
              description="Display a button linking to your complete product catalog"
            />
            
            {content?.showViewAllButton !== false && (
              <div className="space-y-6 pl-4 border-l-2 border-purple-200">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <InputGroup
                    label="Button Text"
                    description="Text displayed on the button"
                  >
                    <TextInput
                      value={content?.viewAllButtonText || 'View All Products'}
                      onChange={(e) => updateContent('viewAllButtonText', e.target.value)}
                      placeholder="View All Products"
                    />
                  </InputGroup>
                  
                  <InputGroup
                    label="Button Link"
                    description="Where the button should navigate to"
                  >
                    <TextInput
                      value={content?.viewAllButtonLink || '/products'}
                      onChange={(e) => updateContent('viewAllButtonLink', e.target.value)}
                      placeholder="/products"
                    />
                  </InputGroup>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <ColorPickerGroup
                    label="Button Background"
                    color={content?.viewAllButtonColor || '#033568'}
                    onChange={(color) => updateContent('viewAllButtonColor', color)}
                  />
                  
                  <ColorPickerGroup
                    label="Button Text Color"
                    color={content?.viewAllButtonTextColor || '#ffffff'}
                    onChange={(color) => updateContent('viewAllButtonTextColor', color)}
                  />
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Spacing Settings */}
      <div className="space-y-4">
        <SectionHeader 
          id="spacing"
          title="Spacing & Background"
          icon={Settings}
          isExpanded={expandedSections.spacing}
          description="Control the overall spacing and background of the section"
        />
        
        {expandedSections.spacing && (
          <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <InputGroup
                label="Top Spacing"
                description="Space above the section (e.g., 4rem, 64px)"
              >
                <TextInput
                  value={content?.marginTop || '4rem'}
                  onChange={(e) => updateContent('marginTop', e.target.value)}
                  placeholder="4rem"
                />
              </InputGroup>
              
              <InputGroup
                label="Bottom Spacing"
                description="Space below the section (e.g., 8rem, 128px)"
              >
                <TextInput
                  value={content?.marginBottom || '8rem'}
                  onChange={(e) => updateContent('marginBottom', e.target.value)}
                  placeholder="8rem"
                />
              </InputGroup>
            </div>
            
            <ColorPickerGroup
              label="Section Background Color"
              color={content?.backgroundColor || '#ffffff'}
              onChange={(color) => updateContent('backgroundColor', color)}
              description="Background color for the entire featured products section"
            />
          </div>
        )}
      </div>
      
      {/* API Settings */}
      <div className="space-y-4">
        <SectionHeader 
          id="api"
          title="Advanced Settings"
          icon={Code}
          isExpanded={expandedSections.api}
          description="Technical configuration - only modify if you know what you're doing"
        />
        
        {expandedSections.api && (
          <div className="bg-white p-6 rounded-xl border border-gray-200 space-y-6">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Info className="w-5 h-5 text-amber-600 mt-0.5" />
                <div>
                  <h5 className="font-medium text-amber-800">For Developers Only</h5>
                  <p className="text-sm text-amber-700 mt-1">
                    These settings control how the component fetches product data. Only modify if you understand the technical implications.
                  </p>
                </div>
              </div>
            </div>
            
            <InputGroup
              label="API Endpoint Path"
              description="The server endpoint that provides featured product data"
            >
              <TextInput
                value={content?.apiPath || '/api/products/basic'}
                onChange={(e) => updateContent('apiPath', e.target.value)}
                placeholder="/api/products/basic"
              />
            </InputGroup>
            
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <h6 className="font-medium text-gray-700 mb-2">Expected API Response Format:</h6>
              <code className="text-xs text-gray-600 block">
                Products should include an "isFeatured" boolean flag
              </code>
            </div>
          </div>
        )}
      </div>
      
     
    </div>
  );
};

export default FeaturedProductsForm;


// // src/components/admin/page-management/section-forms/FeaturedProductsForm.js
// import React from 'react';
// import { Info } from 'lucide-react';
// import ColorPicker from '../../shared/ColorPicker';

// const FeaturedProductsForm = ({ content, updateContent }) => {
//   return (
//     <div className="space-y-6">
//       <h3 className="text-lg font-semibold">Featured Products Section</h3>
      
//       {/* Title Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Title Settings</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showTitle !== false}
//               onChange={(e) => updateContent('showTitle', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Title</span>
//           </label>
//         </div>
        
//         {content?.showTitle !== false && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Title</label>
//               <input
//                 type="text"
//                 value={content?.title || 'Featured Products'}
//                 onChange={(e) => updateContent('title', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Title Color</label>
//               <ColorPicker
//                 color={content?.titleColor || '#111827'}
//                 onChange={(color) => updateContent('titleColor', color)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.showSeparator !== false}
//                   onChange={(e) => updateContent('showSeparator', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Show Separator Lines</span>
//               </label>
//             </div>
            
//             {content?.showSeparator !== false && (
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Separator Color</label>
//                 <ColorPicker
//                   color={content?.separatorColor || '#9CA3AF'}
//                   onChange={(color) => updateContent('separatorColor', color)}
//                 />
//               </div>
//             )}
//           </>
//         )}
//       </div>
      
//       {/* Search Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Search Settings</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.searchEnabled === true}
//               onChange={(e) => updateContent('searchEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Enable Search</span>
//           </label>
//         </div>
        
//         {content?.searchEnabled === true && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Search Placeholder</label>
//             <input
//               type="text"
//               value={content?.searchPlaceholder || 'Search featured products...'}
//               onChange={(e) => updateContent('searchPlaceholder', e.target.value)}
//               className="w-full p-2 border rounded"
//             />
//           </div>
//         )}
//       </div>
      
  
//       {/* Grid Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Grid Settings</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Maximum Products to Display</label>
//           <input
//             type="number"
//             min="1"
//             max="20"
//             value={content?.maxProducts || 5}
//             onChange={(e) => updateContent('maxProducts', parseInt(e.target.value, 10))}
//             className="w-32 p-2 border rounded"
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Grid Gap Size</label>
//           <input
//             type="text"
//             value={content?.gapSize || '1.5rem'}
//             onChange={(e) => updateContent('gapSize', e.target.value)}
//             className="w-full p-2 border rounded"
//             placeholder="e.g., 1.5rem, 24px"
//           />
//         </div>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Mobile Columns</label>
//             <select
//               value={(content?.gridColumns?.mobile || 1).toString()}
//               onChange={(e) => {
//                 const currentGridColumns = content?.gridColumns || {};
//                 updateContent('gridColumns', {
//                   ...currentGridColumns,
//                   mobile: parseInt(e.target.value, 10)
//                 });
//               }}
//               className="w-full p-2 border rounded"
//             >
//               <option value="1">1 Column</option>
//               <option value="2">2 Columns</option>
//             </select>
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Tablet Columns</label>
//             <select
//               value={(content?.gridColumns?.tablet || 2).toString()}
//               onChange={(e) => {
//                 const currentGridColumns = content?.gridColumns || {};
//                 updateContent('gridColumns', {
//                   ...currentGridColumns,
//                   tablet: parseInt(e.target.value, 10)
//                 });
//               }}
//               className="w-full p-2 border rounded"
//             >
//               <option value="1">1 Column</option>
//               <option value="2">2 Columns</option>
//               <option value="3">3 Columns</option>
//             </select>
//           </div>
//         </div>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Desktop Columns</label>
//             <select
//               value={(content?.gridColumns?.desktop || 3).toString()}
//               onChange={(e) => {
//                 const currentGridColumns = content?.gridColumns || {};
//                 updateContent('gridColumns', {
//                   ...currentGridColumns,
//                   desktop: parseInt(e.target.value, 10)
//                 });
//               }}
//               className="w-full p-2 border rounded"
//             >
//               <option value="2">2 Columns</option>
//               <option value="3">3 Columns</option>
//               <option value="4">4 Columns</option>
//             </select>
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Widescreen Columns</label>
//             <select
//               value={(content?.gridColumns?.widescreen || 4).toString()}
//               onChange={(e) => {
//                 const currentGridColumns = content?.gridColumns || {};
//                 updateContent('gridColumns', {
//                   ...currentGridColumns,
//                   widescreen: parseInt(e.target.value, 10)
//                 });
//               }}
//               className="w-full p-2 border rounded"
//             >
//               <option value="3">3 Columns</option>
//               <option value="4">4 Columns</option>
//               <option value="5">5 Columns</option>
//               <option value="6">6 Columns</option>
//             </select>
//           </div>
//         </div>
//       </div>
      
//       {/* Product Card Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Product Card Settings</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Image Aspect Ratio</label>
//           <input
//             type="text"
//             value={content?.imageAspectRatio || '100%'}
//             onChange={(e) => updateContent('imageAspectRatio', e.target.value)}
//             className="w-full p-2 border rounded"
//             placeholder="e.g., 100%, 75%, 4/3"
//           />
//           <p className="text-xs text-gray-500 mt-1">For example, "100%" creates a square, "75%" creates a 4:3 rectangle</p>
//         </div>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showProductDescription === true}
//               onChange={(e) => updateContent('showProductDescription', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Product Description</span>
//           </label>
//         </div>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showPricing !== false}
//               onChange={(e) => updateContent('showPricing', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Price</span>
//           </label>
//         </div>
        
//         {content?.showPricing !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Price Color</label>
//             <ColorPicker
//               color={content?.priceColor || '#2563EB'}
//               onChange={(color) => updateContent('priceColor', color)}
//             />
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.cardShadowEnabled !== false}
//               onChange={(e) => updateContent('cardShadowEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Card Shadow</span>
//           </label>
//         </div>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.cardBorderEnabled !== false}
//               onChange={(e) => updateContent('cardBorderEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Card Border</span>
//           </label>
//         </div>
        
//         {content?.cardBorderEnabled !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Card Border Color</label>
//             <ColorPicker
//               color={content?.cardBorderColor || '#E5E7EB'}
//               onChange={(color) => updateContent('cardBorderColor', color)}
//             />
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.hoverEffectEnabled !== false}
//               onChange={(e) => updateContent('hoverEffectEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Enable Hover Effect</span>
//           </label>
//         </div>
//       </div>
      
//       {/* "View All" Button Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">"View All" Button Settings</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showViewAllButton !== false}
//               onChange={(e) => updateContent('showViewAllButton', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show "View All" Button</span>
//           </label>
//         </div>
        
//         {content?.showViewAllButton !== false && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Text</label>
//               <input
//                 type="text"
//                 value={content?.viewAllButtonText || 'View All Products'}
//                 onChange={(e) => updateContent('viewAllButtonText', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Link</label>
//               <input
//                 type="text"
//                 value={content?.viewAllButtonLink || '/products'}
//                 onChange={(e) => updateContent('viewAllButtonLink', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//               <div>
//                 <label className="block text-sm font-medium mb-1">Button Color</label>
//                 <ColorPicker
//                   color={content?.viewAllButtonColor || '#033568'}
//                   onChange={(color) => updateContent('viewAllButtonColor', color)}
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Button Text Color</label>
//                 <ColorPicker
//                   color={content?.viewAllButtonTextColor || '#ffffff'}
//                   onChange={(color) => updateContent('viewAllButtonTextColor', color)}
//                 />
//               </div>
//             </div>
//           </>
//         )}
//       </div>
      
//       {/* Spacing Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Spacing Settings</h4>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Margin Top</label>
//             <input
//               type="text"
//               value={content?.marginTop || '4rem'}
//               onChange={(e) => updateContent('marginTop', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 4rem, 64px"
//             />
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Margin Bottom</label>
//             <input
//               type="text"
//               value={content?.marginBottom || '8rem'}
//               onChange={(e) => updateContent('marginBottom', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 8rem, 128px"
//             />
//           </div>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Background Color</label>
//           <ColorPicker
//             color={content?.backgroundColor || '#ffffff'}
//             onChange={(color) => updateContent('backgroundColor', color)}
//           />
//         </div>
//       </div>
      
//       {/* API Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <div className="flex justify-between items-center mb-4">
//           <h4 className="font-medium">API Settings</h4>
//           <div className="text-blue-500">
//             <Info size={16} />
//           </div>
//         </div>
        
//         <p className="text-sm text-gray-600 mb-4">
//           These settings are for advanced users. Most users should leave these at their default values.
//         </p>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">API Path</label>
//           <input
//             type="text"
//             value={content?.apiPath || '/api/products/basic'}
//             onChange={(e) => updateContent('apiPath', e.target.value)}
//             className="w-full p-2 border rounded"
//           />
//           <p className="text-xs text-gray-500 mt-1">
//             This endpoint should return products with an "isFeatured" flag.
//           </p>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default FeaturedProductsForm;
















// // src/components/admin/page-management/section-forms/FeaturedProductsForm.js
// import React from 'react';
// import { Info } from 'lucide-react';
// import ColorPicker from '../../shared/ColorPicker';

// const FeaturedProductsForm = ({ content, updateContent }) => {
//   return (
//     <div className="space-y-6">
//       <h3 className="text-lg font-semibold">Featured Products Section</h3>
      
//       {/* Title Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Title Settings</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showTitle !== false}
//               onChange={(e) => updateContent('showTitle', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Title</span>
//           </label>
//         </div>
        
//         {content?.showTitle !== false && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Title</label>
//               <input
//                 type="text"
//                 value={content?.title || 'Featured Products'}
//                 onChange={(e) => updateContent('title', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Title Color</label>
//               <ColorPicker
//                 color={content?.titleColor || '#111827'}
//                 onChange={(color) => updateContent('titleColor', color)}
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="flex items-center">
//                 <input
//                   type="checkbox"
//                   checked={content?.showSeparator !== false}
//                   onChange={(e) => updateContent('showSeparator', e.target.checked)}
//                   className="mr-2"
//                 />
//                 <span className="text-sm">Show Separator Lines</span>
//               </label>
//             </div>
            
//             {content?.showSeparator !== false && (
//               <div className="mb-4">
//                 <label className="block text-sm font-medium mb-1">Separator Color</label>
//                 <ColorPicker
//                   color={content?.separatorColor || '#9CA3AF'}
//                   onChange={(color) => updateContent('separatorColor', color)}
//                 />
//               </div>
//             )}
//           </>
//         )}
//       </div>
      
//       {/* Search Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Search Settings</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.searchEnabled === true}
//               onChange={(e) => updateContent('searchEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Enable Search</span>
//           </label>
//         </div>
        
//         {content?.searchEnabled === true && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Search Placeholder</label>
//             <input
//               type="text"
//               value={content?.searchPlaceholder || 'Search featured products...'}
//               onChange={(e) => updateContent('searchPlaceholder', e.target.value)}
//               className="w-full p-2 border rounded"
//             />
//           </div>
//         )}
//       </div>
      
//       {/* Grid Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Grid Settings</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Maximum Products to Display</label>
//           <input
//             type="number"
//             min="1"
//             max="20"
//             value={content?.maxProducts || 5}
//             onChange={(e) => updateContent('maxProducts', parseInt(e.target.value, 10))}
//             className="w-32 p-2 border rounded"
//           />
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Grid Gap Size</label>
//           <input
//             type="text"
//             value={content?.gapSize || '1.5rem'}
//             onChange={(e) => updateContent('gapSize', e.target.value)}
//             className="w-full p-2 border rounded"
//             placeholder="e.g., 1.5rem, 24px"
//           />
//         </div>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Mobile Columns</label>
//             <select
//               value={content?.gridColumns?.mobile || 1}
//               onChange={(e) => updateContent('gridColumns', {
//                 ...content?.gridColumns,
//                 mobile: parseInt(e.target.value)
//               })}
//               className="w-full p-2 border rounded"
//             >
//               <option value={1}>1 Column</option>
//               <option value={2}>2 Columns</option>
//             </select>
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Tablet Columns</label>
//             <select
//               value={content?.gridColumns?.tablet || 2}
//               onChange={(e) => updateContent('gridColumns', {
//                 ...content?.gridColumns,
//                 tablet: parseInt(e.target.value)
//               })}
//               className="w-full p-2 border rounded"
//             >
//               <option value={1}>1 Column</option>
//               <option value={2}>2 Columns</option>
//               <option value={3}>3 Columns</option>
//             </select>
//           </div>
//         </div>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Desktop Columns</label>
//             <select
//               value={content?.gridColumns?.desktop || 3}
//               onChange={(e) => updateContent('gridColumns', {
//                 ...content?.gridColumns,
//                 desktop: parseInt(e.target.value)
//               })}
//               className="w-full p-2 border rounded"
//             >
//               <option value={2}>2 Columns</option>
//               <option value={3}>3 Columns</option>
//               <option value={4}>4 Columns</option>
//             </select>
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Widescreen Columns</label>
//             <select
//               value={content?.gridColumns?.widescreen || 5}
//               onChange={(e) => updateContent('gridColumns', {
//                 ...content?.gridColumns,
//                 widescreen: parseInt(e.target.value)
//               })}
//               className="w-full p-2 border rounded"
//             >
//               <option value={3}>3 Columns</option>
//               <option value={4}>4 Columns</option>
//               <option value={5}>5 Columns</option>
//               <option value={6}>6 Columns</option>
//             </select>
//           </div>
//         </div>
//       </div>
      
//       {/* Product Card Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Product Card Settings</h4>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Image Aspect Ratio</label>
//           <input
//             type="text"
//             value={content?.imageAspectRatio || '100%'}
//             onChange={(e) => updateContent('imageAspectRatio', e.target.value)}
//             className="w-full p-2 border rounded"
//             placeholder="e.g., 100%, 75%, 4/3"
//           />
//           <p className="text-xs text-gray-500 mt-1">For example, "100%" creates a square, "75%" creates a 4:3 rectangle</p>
//         </div>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showProductDescription === true}
//               onChange={(e) => updateContent('showProductDescription', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Product Description</span>
//           </label>
//         </div>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showPricing !== false}
//               onChange={(e) => updateContent('showPricing', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Price</span>
//           </label>
//         </div>
        
//         {content?.showPricing !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Price Color</label>
//             <ColorPicker
//               color={content?.priceColor || '#2563EB'}
//               onChange={(color) => updateContent('priceColor', color)}
//             />
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.cardShadowEnabled !== false}
//               onChange={(e) => updateContent('cardShadowEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Card Shadow</span>
//           </label>
//         </div>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.cardBorderEnabled !== false}
//               onChange={(e) => updateContent('cardBorderEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show Card Border</span>
//           </label>
//         </div>
        
//         {content?.cardBorderEnabled !== false && (
//           <div className="mb-4">
//             <label className="block text-sm font-medium mb-1">Card Border Color</label>
//             <ColorPicker
//               color={content?.cardBorderColor || '#E5E7EB'}
//               onChange={(color) => updateContent('cardBorderColor', color)}
//             />
//           </div>
//         )}
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.hoverEffectEnabled !== false}
//               onChange={(e) => updateContent('hoverEffectEnabled', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Enable Hover Effect</span>
//           </label>
//         </div>
//       </div>
      
//       {/* "View All" Button Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">"View All" Button Settings</h4>
        
//         <div className="mb-4">
//           <label className="flex items-center">
//             <input
//               type="checkbox"
//               checked={content?.showViewAllButton !== false}
//               onChange={(e) => updateContent('showViewAllButton', e.target.checked)}
//               className="mr-2"
//             />
//             <span className="text-sm">Show "View All" Button</span>
//           </label>
//         </div>
        
//         {content?.showViewAllButton !== false && (
//           <>
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Text</label>
//               <input
//                 type="text"
//                 value={content?.viewAllButtonText || 'View All Products'}
//                 onChange={(e) => updateContent('viewAllButtonText', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="mb-4">
//               <label className="block text-sm font-medium mb-1">Button Link</label>
//               <input
//                 type="text"
//                 value={content?.viewAllButtonLink || '/products'}
//                 onChange={(e) => updateContent('viewAllButtonLink', e.target.value)}
//                 className="w-full p-2 border rounded"
//               />
//             </div>
            
//             <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//               <div>
//                 <label className="block text-sm font-medium mb-1">Button Color</label>
//                 <ColorPicker
//                   color={content?.viewAllButtonColor || '#033568'}
//                   onChange={(color) => updateContent('viewAllButtonColor', color)}
//                 />
//               </div>
//               <div>
//                 <label className="block text-sm font-medium mb-1">Button Text Color</label>
//                 <ColorPicker
//                   color={content?.viewAllButtonTextColor || '#ffffff'}
//                   onChange={(color) => updateContent('viewAllButtonTextColor', color)}
//                 />
//               </div>
//             </div>
//           </>
//         )}
//       </div>
      
//       {/* Spacing Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <h4 className="font-medium mb-4">Spacing Settings</h4>
        
//         <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
//           <div>
//             <label className="block text-sm font-medium mb-1">Margin Top</label>
//             <input
//               type="text"
//               value={content?.marginTop || '4rem'}
//               onChange={(e) => updateContent('marginTop', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 4rem, 64px"
//             />
//           </div>
//           <div>
//             <label className="block text-sm font-medium mb-1">Margin Bottom</label>
//             <input
//               type="text"
//               value={content?.marginBottom || '8rem'}
//               onChange={(e) => updateContent('marginBottom', e.target.value)}
//               className="w-full p-2 border rounded"
//               placeholder="e.g., 8rem, 128px"
//             />
//           </div>
//         </div>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">Background Color</label>
//           <ColorPicker
//             color={content?.backgroundColor || '#ffffff'}
//             onChange={(color) => updateContent('backgroundColor', color)}
//           />
//         </div>
//       </div>
      
//       {/* API Settings */}
//       <div className="bg-gray-50 p-4 rounded-md">
//         <div className="flex justify-between items-center mb-4">
//           <h4 className="font-medium">API Settings</h4>
//           <div className="text-blue-500">
//             <Info size={16} />
//           </div>
//         </div>
        
//         <p className="text-sm text-gray-600 mb-4">
//           These settings are for advanced users. Most users should leave these at their default values.
//         </p>
        
//         <div className="mb-4">
//           <label className="block text-sm font-medium mb-1">API Path</label>
//           <input
//             type="text"
//             value={content?.apiPath || '/api/products/basic'}
//             onChange={(e) => updateContent('apiPath', e.target.value)}
//             className="w-full p-2 border rounded"
//           />
//           <p className="text-xs text-gray-500 mt-1">
//             This endpoint should return products with an "isFeatured" flag.
//           </p>
//         </div>
//       </div>
//     </div>
//   );
// };

// export default FeaturedProductsForm;